import { Box, Button, Typography, BoxProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ProductCardVariant } from '../../../constants/common';

interface SeeAllButtonProps {
  platform: string | null;
  fontSize: number;
  fontWeight: number;
  showArrow: boolean;
}

interface ProductContainerProps extends BoxProps {
  productCardVariant: ProductCardVariant;
  isHorizonVertical?: boolean;
}

interface TitleTextProps {
  fontSize: number;
  fontWeight: number;
  fontFamily?: string;
  fontStyle?: string;
  lineHeight?: string;
  fontColor?: string;
  titleDisplay?: string;
  titleAlignItems?: string;
  titleHeight?: string;
}

interface TitleContainerProps {
  isHorizonLayout?: boolean;
}

export const TitleContainer = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isHorizonLayout',
})<TitleContainerProps>(({ theme, isHorizonLayout }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginLeft: isHorizonLayout ? theme.spacing(1) : 0,
}));

export const TitleText = styled(Typography, {
  shouldForwardProp: (prop) =>
    prop !== 'fontSize' &&
    prop !== 'fontWeight' &&
    prop !== 'fontFamily' &&
    prop !== 'fontStyle' &&
    prop !== 'lineHeight' &&
    prop !== 'fontColor' &&
    prop !== 'titleDisplay' &&
    prop !== 'titleAlignItems' &&
    prop !== 'titleHeight',
})<TitleTextProps>(
  ({
    fontSize,
    fontWeight,
    fontFamily,
    fontStyle,
    lineHeight,
    fontColor,
    titleDisplay,
    titleAlignItems,
    titleHeight,
  }) => ({
    fontSize,
    fontWeight,
    fontFamily,
    fontStyle,
    lineHeight,
    color: fontColor,
    display: titleDisplay,
    alignItems: titleAlignItems,
    height: titleHeight,
  }),
);

export const CarouselContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'platform' && prop !== 'showArrow' && prop !== 'isVerticalGrid',
})<{ platform: string | null; showArrow: boolean; isVerticalGrid?: boolean }>(
  ({ theme, platform, showArrow, isVerticalGrid }) => ({
    padding: isVerticalGrid ? theme.spacing(1, 1, 0, 1) : '1rem 1rem 1rem 1rem',
    backgroundColor: isVerticalGrid ? theme.palette.grey[50] : theme.palette.common.white,
    ...(platform === 'app' &&
      !showArrow && {
        paddingRight: isVerticalGrid ? theme.spacing(1) : '0',
      }),
    '&.searchTopPicksApp': {
      padding: '0',
      ...(platform === 'app' && {
        padding: '0',
      }),
    },
  }),
);

export const BottomSheetBackdrop = styled('div')(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  zIndex: theme.zIndex.modal - 1,
  backgroundColor: 'rgba(0, 0, 0, 0.32)',
  touchAction: 'none',
}));

export const ProductContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'productCardVariant' && prop !== 'isHorizonVertical',
})<ProductContainerProps>(({ theme, productCardVariant, isHorizonVertical }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: isHorizonVertical ? 'center' : 'stretch',
  flex: isHorizonVertical ? '0 0 auto' : 1,
  width: isHorizonVertical ? '100%' : '-webkit-fill-available',
  maxWidth: isHorizonVertical ? '100%' : undefined,
  minWidth: isHorizonVertical ? 0 : undefined,
  position: 'relative',
  paddingLeft: !isHorizonVertical && productCardVariant === ProductCardVariant.COMPACT ? theme.spacing(1) : 0,
  ...(!isHorizonVertical &&
    productCardVariant === ProductCardVariant.COMPACT && {
      [theme.breakpoints.down('sm')]: {
        height: theme.spacing(32),
      },
    }),
}));

export const SeeAllButton = styled(Button, {
  shouldForwardProp: (prop) =>
    prop !== 'platform' && prop !== 'fontSize' && prop !== 'fontWeight' && prop !== 'showArrow',
})<SeeAllButtonProps>(({ theme, platform, fontSize, fontWeight, showArrow }) => ({
  background: 'none',
  display: 'flex',
  minWidth: '80px',
  alignItems: 'center',
  flexDirection: 'row',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: theme.palette.grey[900],
  textDecoration: 'none',
  textTransform: 'none',
  gap: platform === 'app' ? 0 : 2,
  fontSize,
  fontFamily: 'Noto Sans Thai,sans-serif',
  fontWeight,
  ...(!showArrow && {
    paddingRight: '1rem',
  }),
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

export const StyledAdBanner = styled(Box)(({ theme }) => ({
  borderRadius: '2px',
  display: 'flex',
  height: '10px',
  minWidth: '12px',
  fontSize: '8px',
  textAlign: 'center',
  top: '8px',
  left: '5px',
  position: 'absolute',
  color: theme.palette.grey[400],
  padding: '0px 2px 0px 2px',
  background: theme.palette.grey[50],
  '&.isSponsoredProduct': {
    margin: '4px 0px 0px 10px',
    zIndex: 1,
  },
}));
